import React, { useMemo, useState } from "react";
import { Label } from "@radix-ui/react-label";
import { dateValue, mapNameOptions, SectionTitle } from "../EmployeeForm";
import { useSelector } from "react-redux";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { toIST } from "../../../utils/calculateMargin";
import BASEURL from "../../../BaseUrl";
const titleOptions = ["Mr.", "Ms.", "Mrs.", "Dr.", "Prof."].map((x) => ({
  label: x,
  value: x,
}));

const genderOptions = ["Male", "Female", "Others"].map((x) => ({
  label: x,
  value: x,
}));

const maritalStatusOptions = ["Married", "Single", "Others"].map((x) => ({
  label: x,
  value: x,
}));

const bloodGroupOptions = [
  "A+",
  "A-",
  "B+",
  "B-",
  "O+",
  "O-",
  "AB+",
  "AB-",
].map((x) => ({ label: x, value: x }));

function BasicInfo({ formData, handleUpload, handleOnChange, UploadLoader }) {
  const [logoPreview, setLogoPreview] = useState(null);
  const { nationality } = useSelector((state) => state.masterData);
  const nationalityOptions = useMemo(
    () => mapNameOptions(nationality),
    [nationality],
  );
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Basic Info</SectionTitle>
      <div className="w-full flex flex-col gap-2 px-3">
        <Label>Profile Picture (1:1)</Label>
        <img
          src={logoPreview || `${BASEURL}${formData?.profilePhoto}`}
          alt=""
          className="w-40 h-40 object-cover rounded-full border"
        />
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setLogoPreview(URL.createObjectURL(file));
            handleUpload(
              file,
              (url) => handleOnChange("profilePhoto", url),
              "profilePhoto",
            );
          }}
        />
        <UploadLoader id="profilePhoto" />
        <p className="text-xs text-gray-500">
          Recommended size: <b>1024 x 1024</b>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 w-full">
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Employee ID <span className="text-red-600">*</span>
          </Label>
          <InputText
            value={formData.employeeId}
            onChange={(e) => handleOnChange("employeeId", e.target.value)}
            placeholder="Enter Employee ID"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex gap-3">
          <div className="min-w-[6.5rem] max-w-[6.5rem] flex flex-col gap-1">
            <Label className="px-3">
              Title <span className="text-red-600">*</span>
            </Label>
            <Dropdown
              value={formData.title}
              options={titleOptions}
              onChange={(e) => handleOnChange("title", e.value)}
              placeholder="Title"
              className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
            />
          </div>
          <div className="w-full flex flex-col gap-1">
            <Label className="px-3">
              First Name <span className="text-red-600">*</span>
            </Label>
            <InputText
              value={formData.firstName}
              onChange={(e) => handleOnChange("firstName", e.target.value)}
              placeholder="Enter First Name"
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
            />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-full flex flex-col gap-1">
            <Label className="px-3">Middle Name</Label>
            <InputText
              value={formData.middleName}
              onChange={(e) => handleOnChange("middleName", e.target.value)}
              placeholder="Enter Middle Name"
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
            />
          </div>
          <div className="w-full flex flex-col gap-1">
            <Label className="px-3">Last Name</Label>
            <InputText
              value={formData.lastName}
              onChange={(e) => handleOnChange("lastName", e.target.value)}
              placeholder="Enter Last Name"
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 w-full">
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Personal Email <span className="text-red-600">*</span>
          </Label>
          <InputText
            type="email"
            value={formData.personalEmail}
            onChange={(e) => handleOnChange("personalEmail", e.target.value)}
            placeholder="Enter Personal Email"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Personal Mobile <span className="text-red-600">*</span>
          </Label>
          <InputText
            value={formData.personalMobile}
            onChange={(e) => handleOnChange("personalMobile", e.target.value)}
            placeholder="10 digit mobile"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">Official Email</Label>
          <InputText
            type="email"
            value={formData.officialEmail}
            onChange={(e) => handleOnChange("officialEmail", e.target.value)}
            placeholder="Enter Official Email"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">Official Mobile</Label>
          <InputText
            value={formData.officialMobile}
            onChange={(e) => handleOnChange("officialMobile", e.target.value)}
            placeholder="10 digit mobile"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 w-full">
        <div className="w-full flex flex-col gap-1">
          <Label className="px-3">Father's Name</Label>
          <InputText
            value={formData.fatherName}
            onChange={(e) => handleOnChange("fatherName", e.target.value)}
            placeholder="Enter Father's Name"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="w-full flex flex-col gap-1">
          <Label className="px-3">Mother's Name</Label>
          <InputText
            value={formData.motherName}
            onChange={(e) => handleOnChange("motherName", e.target.value)}
            placeholder="Enter Mother's Name"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="w-full flex flex-col gap-1">
          <Label className="px-3">Marital Status</Label>
          <Dropdown
            value={formData.maritalStatus}
            onChange={(e) => handleOnChange("maritalStatus", e.target.value)}
            options={maritalStatusOptions}
            placeholder="Select Marital Status"
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        {formData.maritalStatus === "Married" && (
          <div className="w-full flex flex-col gap-1">
            <Label className="px-3">Spouse's Name</Label>
            <InputText
              value={formData.spouseName}
              onChange={(e) => handleOnChange("spouseName", e.target.value)}
              placeholder="Enter Spouse's Name"
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 w-full">
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Gender <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.gender}
            options={genderOptions}
            onChange={(e) => handleOnChange("gender", e.value)}
            placeholder="Select Gender"
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Date of Birth <span className="text-red-600">*</span>
          </Label>
          <Calendar
            value={dateValue(formData.dob)}
            onChange={(e) => handleOnChange("dob", toIST(e.value))}
            maxDate={new Date()}
            showIcon
            dateFormat="dd/mm/yy"
            placeholder="Select DOB"
            className="w-full bg-input border border-gray-300 rounded-md"
            inputClassName="w-full px-3 py-1.5 bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Nationality <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.nationality}
            options={nationalityOptions}
            onChange={(e) => handleOnChange("nationality", e.value)}
            placeholder="Select Nationality"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Blood Group <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.bloodGroup}
            options={bloodGroupOptions}
            onChange={(e) => handleOnChange("bloodGroup", e.value)}
            placeholder="Select Blood Group"
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
      </div>
    </section>
  );
}

export default BasicInfo;
